import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
  Grid,
  CircularProgress
} from '@mui/material';
import { collection, query, getDocs } from 'firebase/firestore';
import { db, functions } from '../../../../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { SUBSCRIPTION_TYPES } from './constants';
import './dialogs.css';

const EditDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  formData, 
  setFormData, 
  selectedUser,
  selectedSubscriptionType
}) => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState({
    startDate: false,
    endDate: false,
    mentor: false
  });
  
  const subscriptionInfo = SUBSCRIPTION_TYPES[selectedSubscriptionType.toUpperCase()];
  const currentSubscription = selectedUser?.subscriptions[selectedSubscriptionType];

  // # Initialize form data with current subscription values when dialog opens
  useEffect(() => {
    if (open && currentSubscription) {
      const initialFormData = {
        ...formData,
        startDate: currentSubscription.startDate || new Date().toISOString(),
        endDate: currentSubscription.endDate || new Date().toISOString(),
        mentor: currentSubscription.mentor || null
      };
      
      setFormData(initialFormData);
      setOriginalData(initialFormData);
      setHasChanges({
        startDate: false,
        endDate: false,
        mentor: false
      });
    }
  }, [open, currentSubscription, setFormData]);

  // # Fetch available admins when dialog opens for mentorship subscriptions
  useEffect(() => {
    const fetchAdmins = async () => {
      if (!subscriptionInfo.requiresMentor) return;
      
      try {
        setIsLoading(true);
        // # Query the administration/admins document
        const adminsDoc = await getDocs(collection(db, 'administration'));
        const adminEmails = adminsDoc.docs[0]?.data()?.emails || [];
        
        // # Get admin user details
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        
        // # Filter and map admin users with complete information
        const adminsList = usersSnapshot.docs
          .filter(doc => adminEmails.includes(doc.data().email))
          .map(doc => ({
            uid: doc.id,
            email: doc.data().email,
            name: `${doc.data().firstName} ${doc.data().lastName}`.trim(),
            firstName: doc.data().firstName || '',
            lastName: doc.data().lastName || ''
          }));
        
        setAdmins(adminsList);
      } catch (error) {
        console.error('Error fetching admins:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchAdmins();
    }
  }, [open, subscriptionInfo.requiresMentor]);

  // # Handle mentor selection
  const handleMentorChange = (event) => {
    const selectedMentorData = admins.find(admin => admin.email === event.target.value);
    if (selectedMentorData) {
      setFormData({
        ...formData,
        mentor: {
          uid: selectedMentorData.uid,
          email: selectedMentorData.email,
          name: selectedMentorData.name,
          firstName: selectedMentorData.firstName,
          lastName: selectedMentorData.lastName
        }
      });
      setHasChanges({
        ...hasChanges,
        mentor: true
      });
    }
  };

  // # Get current mentor display
  const getCurrentMentorDisplay = () => {
    const mentor = currentSubscription?.mentor;
    if (!mentor) return 'None assigned';
    if (mentor === 'none') return 'None assigned';
    if (typeof mentor === 'string') return mentor;
    if (typeof mentor === 'object' && mentor.name) {
      return `${mentor.name} (${mentor.email})`;
    }
    return 'None assigned';
  };

  // # Calculate status based on dates
  const calculateStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) return 'pending';
    if (now > end) return 'expired';
    return 'active';
  };

  // # Handle form submission for batch write
  const handleSubmit = () => {
    // # Prepare data for batch write in subscription_management.js
    const updateData = {
      ...currentSubscription
    };
    
    // # Only update fields that have changed
    if (hasChanges.startDate) {
      updateData.startDate = formData.startDate;
    }
    
    if (hasChanges.endDate) {
      updateData.endDate = formData.endDate;
    }
    
    if (hasChanges.mentor) {
      updateData.mentor = formData.mentor || null;
    }
    
    // # Always recalculate status to ensure it's correct
    // # Use the most up-to-date start and end dates (either from form or current subscription)
    const startDateToUse = updateData.startDate || currentSubscription.startDate;
    const endDateToUse = updateData.endDate || currentSubscription.endDate;
    
    if (startDateToUse && endDateToUse) {
      const newStatus = calculateStatus(startDateToUse, endDateToUse);
      updateData.status = newStatus;
      
      // # Add a flag to prevent duplicate notifications from the watchSubscriptionChanges function
      updateData.metadata = {
        ...(updateData.metadata || {}),
        manuallyEdited: true,
        lastEditedAt: new Date().toISOString()
      };
      
      // # If status changed or dates changed, send notification
      if (newStatus !== currentSubscription.status || 
          hasChanges.startDate || 
          hasChanges.endDate) {
        
        // # Determine the action based on status change and date comparisons
        let action = 'update';
        if (newStatus === 'active' && currentSubscription.status !== 'active') {
          action = 'activate';
        } else if (newStatus === 'inactive' && currentSubscription.status === 'active') {
          action = 'deactivate';
        } else if (newStatus === 'active' && currentSubscription.status === 'active' && hasChanges.endDate) {
          // # Compare the new end date with the current end date
          const newEndDate = new Date(endDateToUse);
          const currentEndDate = new Date(currentSubscription.endDate);
          action = newEndDate > currentEndDate ? 'extend' : 'reduce';
        }
        
        // # Create notification for the subscription update
        const createNotification = httpsCallable(functions, 'createSubscriptionNotification');
        createNotification({
          uid: selectedUser.uid,
          subscriptionType: selectedSubscriptionType,
          startDate: startDateToUse,
          endDate: endDateToUse,
          action: action,
          manuallyTriggered: true // # Add flag to indicate this was manually triggered
        })
        .then(notificationResult => {
          console.log(`Subscription ${action} notification sent:`, notificationResult.data);
        })
        .catch(error => {
          console.error(`Error sending subscription ${action} notification:`, error);
        });
      }
    }
    
    // # Pass the update data to parent component for batch processing
    onSubmit(updateData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="dialog-title">
        Edit {subscriptionInfo.name}
        <Typography variant="body2" className="dialog-subtitle">
          {selectedUser?.userName} ({selectedUser?.email})
        </Typography>
      </DialogTitle>
      
      <DialogContent className="dialog-content">
        <div className="current-info">
          <Typography variant="subtitle2" color="textSecondary">
            Current Subscription Details
          </Typography>
          <Typography>
            Type: {subscriptionInfo.name}
          </Typography>
          <Typography>
            Status: {currentSubscription?.status}
          </Typography>
          <Typography>
            Valid until: {currentSubscription?.endDate ? new Date(currentSubscription.endDate).toLocaleDateString() : 'N/A'}
          </Typography>
          {subscriptionInfo.requiresMentor && (
            <Typography>
              Current Mentor: {getCurrentMentorDisplay()}
            </Typography>
          )}
        </div>

        <Grid container spacing={2} className="form-field">
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Start Date"
              type="datetime-local"
              value={formData.startDate?.slice(0, 16) || ''}
              onChange={(e) => {
                const newStartDate = new Date(e.target.value).toISOString();
                setFormData({
                  ...formData,
                  startDate: newStartDate
                });
                setHasChanges({
                  ...hasChanges,
                  startDate: newStartDate !== originalData?.startDate
                });
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="End Date"
              type="datetime-local"
              value={formData.endDate?.slice(0, 16) || ''}
              onChange={(e) => {
                const newEndDate = new Date(e.target.value).toISOString();
                setFormData({
                  ...formData,
                  endDate: newEndDate
                });
                setHasChanges({
                  ...hasChanges,
                  endDate: newEndDate !== originalData?.endDate
                });
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          {subscriptionInfo.requiresMentor && (
            <Grid item xs={12}>
              {isLoading ? (
                <div className="loading-container">
                  <CircularProgress size={24} />
                  <Typography>Loading admin mentors...</Typography>
                </div>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Select Admin as Mentor</InputLabel>
                  <Select
                    value={formData.mentor?.email || ''}
                    onChange={handleMentorChange}
                    label="Select Admin as Mentor"
                  >
                    {admins.map((admin) => (
                      <MenuItem key={admin.uid} value={admin.email}>
                        {admin.name} ({admin.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          )}
        </Grid>

        <div className="info-text">
          Note: Changes will take effect immediately.
        </div>
      </DialogContent>

      <DialogActions className="dialog-actions">
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained" 
          color="primary"
          disabled={isLoading || (!hasChanges.startDate && !hasChanges.endDate && !hasChanges.mentor)}
        >
          Update Subscription
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
